import { Amplify, ResourcesConfig } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser, signInWithRedirect, AuthSession } from 'aws-amplify/auth';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { configureCloudwatchRUM } from 'src/analytics/CloudWatchRumClient';
import { configureKatalLogger, entryLog, logger } from 'src/analytics/KatalLogger';
import { DEVELOPER_LDAP_GROUP, eLocalStorageKeys } from 'src/constants/generic-constants';
import { Status, UserAuthContext } from 'src/models/AuthContextModels';
import { configureAgGridLicense } from 'src/utils/ag-grid-license-config';
import { configureAgStudioLicense } from 'src/utils/ag-studio-license-config';
import { getAmplifyAuthConfig, getApiConfig, getAppSyncConfig } from 'src/utils/amplify-config';
import { handlePostAuthRedirect, setPostAuthRedirect } from 'src/utils/auth-redirect';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

// Define the shape of your context including the new functions
interface AuthContextValue extends UserAuthContext {
  updateUserAuthDetails: (updates: Partial<UserAuthContext>, fromModifyAuthContext: boolean) => void;
  resetToDefaultAuthDetails: () => void;
}

export const authInitialState: UserAuthContext = {
  userAlias: '',
  userDisplayName: '',
  userGivenName: '',
  userEmail: '',
  isDev: false,
  isInitiallyDev: false,
  isUserRoleModified: false,
  userAuthenticationStatus: Status.NotInitiated
};

const initialAuthContextValue: AuthContextValue = {
  ...authInitialState,
  updateUserAuthDetails: () => {},
  resetToDefaultAuthDetails: () => {}
};

const AuthContextDetails = createContext<AuthContextValue>(initialAuthContextValue);

export const useAuth = () => useContext(AuthContextDetails);

// Provider component that wraps your app and makes auth object
export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userCognitoAuthData, updateUserAuthDetails, resetToDefaultAuthDetails } = useAuthProvider();
  const [isInitializationComplete, setInitializationComplete] = useState(false);

  useEffect(() => {
    const configureLoggerAndServices = async () => {
      if (userCognitoAuthData?.userAuthenticationStatus === Status.Completed) {
        await configureKatalLogger(userCognitoAuthData.userAlias, userCognitoAuthData.userDisplayName, userCognitoAuthData.isDev);

        await entryLog();

        // License configuration is non-critical to app boot and each license is
        // independent: a failure of one must not block the other or app init.
        try {
          await configureAgGridLicense();
        } catch (error: unknown) {
          logger.error('AG Grid license configuration failed; continuing without it', {
            logType: 'configStatus',
            error: error instanceof Error ? error.message : String(error)
          });
        }
        try {
          await configureAgStudioLicense();
        } catch (error: unknown) {
          logger.error('AG Studio license configuration failed; continuing without it', {
            logType: 'configStatus',
            error: error instanceof Error ? error.message : String(error)
          });
        }

        configureCloudwatchRUM(userCognitoAuthData.userAlias);
        handlePostAuthRedirect();
        setInitializationComplete(true);
      }
    };

    configureLoggerAndServices();
  }, [userCognitoAuthData]);

  if (userCognitoAuthData?.userAuthenticationStatus !== Status.Completed || !isInitializationComplete) {
    return <LoadingSpinner />;
  }

  return (
    <AuthContextDetails.Provider value={{ ...userCognitoAuthData, updateUserAuthDetails, resetToDefaultAuthDetails }}>
      {children}
    </AuthContextDetails.Provider>
  );
};

const useAuthProvider = () => {
  const [userCognitoAuthData, setUserCognitoAuthData] = useState<UserAuthContext>({
    ...authInitialState,
    userAuthenticationStatus: Status.Loading
  });

  useEffect(() => {
    authenticateAndSetUserDetails();
  }, []);

  const authenticateAndSetUserDetails = () => {
    Amplify.configure(getAmplifyAuthConfig());

    const configureAppSync = async () => {
      try {
        const authConfig = getAmplifyAuthConfig();
        const awsAppSync = await getAppSyncConfig();

        const apiConfig = getApiConfig();
        const combinedConfig = {
          ...authConfig,
          API: {
            ...awsAppSync.API,
            ...apiConfig.API
          }
        };

        Amplify.configure(combinedConfig as ResourcesConfig, {
          API: {
            GraphQL: {
              headers: async () => {
                const session = await fetchAuthSession();
                return {
                  Authorization: session.tokens?.idToken?.toString() ?? ''
                };
              }
            }
          }
        });
      } catch (error: any) {
        console.error('Unable to fetch Config', error);
      }
    };

    const signInWithAmazonFederate = async () => {
      try {
        setPostAuthRedirect();
        await signInWithRedirect({
          provider: { custom: 'AmazonFederate' }
        });
      } catch (error: any) {
        console.error('Unable to sign in with AmazonFederate', error);
        setUserCognitoAuthData({
          ...userCognitoAuthData,
          userAuthenticationStatus: Status.Loading
        });
      }
    };

    getCurrentUser()
      .then(async () => {
        await configureAppSync();
        const session = await fetchAuthSession();
        const sessionDetails = await getSessionDetails(session);
        setUserCognitoAuthData(sessionDetails);
      })
      .catch(() => {
        signInWithAmazonFederate();
      });
  };

  /**
   * Updates the user authentication details and saves them to local storage.
   * If called from ModifyAuthContext, it sets isUserRoleModified to true.
   */
  const updateUserAuthDetails = (updates: Partial<UserAuthContext>, fromModifyAuthContext: boolean) => {
    setUserCognitoAuthData((prevState) => {
      const updatedState = { ...prevState, ...updates };
      if (fromModifyAuthContext) {
        updatedState.isUserRoleModified = true;
      }
      localStorage.setItem(eLocalStorageKeys.UserAuthDetails, JSON.stringify(updatedState));
      return updatedState;
    });
  };

  // Reset to default auth details
  const resetToDefaultAuthDetails = () => {
    authenticateAndSetUserDetails(); // Re-authenticate or set to initial state
    localStorage.removeItem(eLocalStorageKeys.UserAuthDetails); // Clear saved details
  };

  /**
   * Retrieves and formats the LDAP groups from the credentials payload.
   * @param session The AWS auth session object containing the ID token payload.
   * @returns A properly formatted string array of LDAP groups.
   */
  const formatUserLDAPs = (session: AuthSession): string[] => {
    // if user is not part of any ldap, we receive it as "[]"
    const ldapGroups = session.tokens?.idToken?.payload?.['custom:LDAP_GROUPS'];
    // Check if ldapGroups is a string and try to parse it as JSON
    if (typeof ldapGroups === 'string') {
      try {
        const parsedLdapGroups = JSON.parse(ldapGroups);
        return Array.isArray(parsedLdapGroups) && parsedLdapGroups.every((item) => typeof item === 'string') ? (parsedLdapGroups as string[]) : [];
      } catch (error: any) {
        console.error('Failed to parse ldapGroups string: ', error);
        return [];
      }
    }

    // If ldapGroups is already an array, return it; otherwise, return an empty array
    return Array.isArray(ldapGroups) && ldapGroups.every((item) => typeof item === 'string') ? (ldapGroups as string[]) : [];
  };

  const getSessionDetails = async (session: AuthSession) => {
    const userLDAPs: string[] = formatUserLDAPs(session);
    const idTokenPayload = session.tokens?.idToken?.payload;

    const identities = idTokenPayload?.['identities'];
    const userId = Array.isArray(identities) && identities.length > 0 ? (identities[0] as any)?.userId : undefined;

    // Always start with extracting the default session details from the credentials
    const defaultSessionDetails: UserAuthContext = {
      userAlias: userId,
      userDisplayName: idTokenPayload?.['custom:DISPLAY_NAME'] as string,
      userGivenName: idTokenPayload?.['custom:GIVEN_NAME'] as string,
      userEmail: idTokenPayload?.['custom:EMAIL'] as string,
      isInitiallyDev: userLDAPs.includes(DEVELOPER_LDAP_GROUP),
      isDev: userLDAPs.includes(DEVELOPER_LDAP_GROUP),
      isUserRoleModified: false,
      userAuthenticationStatus: Status.Completed
    };

    // Check localStorage for any developer modifications
    const savedAuthDetails = localStorage.getItem(eLocalStorageKeys.UserAuthDetails);
    if (savedAuthDetails && JSON.parse(savedAuthDetails)?.isUserRoleModified) {
      console.debug('Identified as custom modified. so returning from local storage');
      const parsedDetails: UserAuthContext = JSON.parse(savedAuthDetails);
      return parsedDetails; // Return the modified session details
    }

    console.debug('Identified as non modified. so returning from cognito');
    return defaultSessionDetails;
  };

  return { userCognitoAuthData, updateUserAuthDetails, resetToDefaultAuthDetails };
};
